import React, { Component, Fragment } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { Button, FormGroup, Form, Row, Col, ModalFooter, Label } from 'reactstrap';
import Select from 'react-select';
import OutlineClear from 'react-md-icon/dist/OutlineClear';
import DriveFile from 'react-md-icon/dist/BaselineInsertDriveFile';
import * as Yup from 'yup';
import { Formik, Field } from 'formik';

import {
  displayToaster,
  hideToaster, toggleDrawer,
  GET_DOCUMENT_PREVIEW_URL,
  clearLoadingState,
  updateEmployeeDocument, UPDATE_EMPLOYEE_DOCUMENT, GET_UPLOAD_URL,
  LOADING_PROP_TYPE, getEmployeeDocCategories, GET_EMPLOYEE_DOCUMENT_CATEGORIES,
  clearCategories,
} from '../../modules';
import {
  Drawer,
  DrawerHeader,
  UnsupportedError,
  LockedModalBody,
  UploadDropzone,
  FieldBuilder,
  SubmitButton,
  ErrorMessage,
  FileViewer,
  Spinner,
} from '../../components';
import {
  APPLICATION_ROOT, IMAGE_TYPES,
  ROLE_BASE_MANAGER,
  AUTO_GENERATED_FILE_CATEGORIES,
  MAX_EMPLOYEE_DOC_UPLOAD_SIZE_IN_BYTES,
  MAX_DOC_CATEGORY_LENGTH,
  PENDING_STEP_NUMBER,
  SIGNED_STEP_NUMBER,
} from '../../constants';
import { downloadFromUrl, setFileUrl, checkRole } from '../../helpers';
import './EditEmployeeDocument.scss';

const DEFAULT_STATE = {
  file: undefined,
  fileCleared: false,
};

class EditEmployeeDocument extends Component {
  constructor(props) {
    super(props);

    this.state = DEFAULT_STATE;

    this.handleDrawer = this.handleDrawer.bind(this);
    this.onDrop = this.onDrop.bind(this);
    this.onClear = this.onClear.bind(this);
  }

  componentDidUpdate(prevProps) {
    const {
      loading,
      selectedDocument,
      drawerIsOpen,
      docCategories: {
        categories,
      },
    } = this.props;
    const { loading: prevLoading, drawerIsOpen: prevDrawerIsOpen } = prevProps;
    if (prevLoading[UPDATE_EMPLOYEE_DOCUMENT] === 'loading' && loading[UPDATE_EMPLOYEE_DOCUMENT] === 'complete') {
      this.props.displayToaster([
        'success',
        <FormattedMessage id="DOCUMENT_UPDATED" />,
        'app',
        5000,
        true,
      ]);
      this.handleDrawer();
    }
    if (
      drawerIsOpen &&
      selectedDocument &&
      selectedDocument.id &&
      loading[GET_EMPLOYEE_DOCUMENT_CATEGORIES] !== 'loading' &&
      (!prevLoading[GET_EMPLOYEE_DOCUMENT_CATEGORIES] || prevLoading[GET_EMPLOYEE_DOCUMENT_CATEGORIES] === 'complete')
    ) {
      this.props.getDocCategoryList(undefined, selectedDocument.companyId, selectedDocument.employeeId);
    }
    if (prevDrawerIsOpen && !drawerIsOpen && categories.length > 0) {
      this.props.clearCategories();
    }
  }

  onSubmit = (values) => {
    const { selectedDocument: { id, employeeId, companyId } } = this.props;
    const { file } = this.state;
    const { title, togglePrivate, category } = values;
    this.props.updateEmployeeDocument(file, companyId, employeeId, title, togglePrivate, id, category);
  }

  onDrop(file) {
    this.setState({ file });
  }

  onClear() {
    this.setState({ fileCleared: true });
  }

  handleDrawer() {
    const { drawerIsOpen } = this.props;

    this.props.toggleDrawer(!drawerIsOpen);
    this.props.hideToaster('drawer');
    this.props.clearLoadingState(UPDATE_EMPLOYEE_DOCUMENT);
    // Allowing the drawer to close before resetting state makes for a cleaner animation
    setTimeout(() => this.setState(DEFAULT_STATE), 500);
  }

  loadMoreCategories() {
    const { docCategories: { next } } = this.props;
    if (next && this.props.loading[GET_EMPLOYEE_DOCUMENT_CATEGORIES] === 'complete') {
      this.props.getDocCategoryList(next, this.props.selectedDocument.companyId);
    }
  }

  groupCategories = (docCategories) => {
    const { recent, categories } = docCategories;
    return [
      { label: 'RECENT', options: recent },
      { label: 'CATEGORIES', options: categories },
    ];
  }

  render() {
    const {
      loading,
      drawerIsOpen,
      selectedDocument,
      filePreview: { url, mimeType },
      userRoles,
      docCategories,
    } = this.props;
    const {
      file,
      fileCleared,
    } = this.state;
    const isFormLocked =
      selectedDocument.isPublishedToEmployee ||
      selectedDocument.isOnboarding ||
      selectedDocument.isEsignatureDocument ||
      (AUTO_GENERATED_FILE_CATEGORIES.filter(category => category === selectedDocument.category).length > 0 &&
        selectedDocument.uploadedBy === 'Onboarding') ||
      (selectedDocument.category === 'I-9' && selectedDocument.title === 'FormI9') ||
      !checkRole(userRoles, ROLE_BASE_MANAGER);
    const isSubmitting = loading[UPDATE_EMPLOYEE_DOCUMENT] === 'loading' || loading[GET_UPLOAD_URL] === 'loading';
    const submitDisabled = loading[GET_DOCUMENT_PREVIEW_URL] === 'error';

    const containerClasses = ['w-100', 'h-100'];
    const buttonClasses = ['row-action', 'upload-cancel', 'position-absolute'];
    // don't display X button for signed docs
    if (selectedDocument && selectedDocument.status && selectedDocument.status.stepNumber === SIGNED_STEP_NUMBER) {
      buttonClasses.push('d-none');
    } else {
      buttonClasses.push('d-flex');
    }
    const iconStyles = { fontSize: '1.5rem', color: 'var(--color-neutral-500)' };
    const largeIconStyles = { fontSize: '5rem', color: 'var(--color-neutral-500)', width: '100%' };
    const isLoading = loading[GET_DOCUMENT_PREVIEW_URL] === 'loading';
    if (isLoading) {
      containerClasses.push('hello-sign-container');
      containerClasses.push('loading');
    }

    const fileUrl = setFileUrl(url);

    let documentPreview;
    const previewRowClasses = new Set(['align-items-center', 'p-3', 'h-75']);
    const previewColClasses = new Set(['h-100', 'p-0']);
    if (!isLoading) {
      previewColClasses.add('border-dashed');
      if (!IMAGE_TYPES.includes(mimeType)) previewRowClasses.delete('h-75');
      if (selectedDocument && selectedDocument.status && selectedDocument.status.stepNumber === PENDING_STEP_NUMBER) {
        previewRowClasses.delete('h-75');
        previewColClasses.delete('border-dashed');
        documentPreview = (
          <div>
            <DriveFile style={largeIconStyles} />
            <h5 className="text-center pt-3"><FormattedMessage id="PREVIEW_UNAVAILABLE" /></h5>
            <div className="text-center text-muted"><FormattedMessage id="PREVIEW_UNAVAILABLE_PENDING" /></div>
          </div>
        );
      } else if (selectedDocument && selectedDocument.status && selectedDocument.status.isProcessing === true) {
        previewRowClasses.delete('h-75');
        previewColClasses.delete('border-dashed');
        documentPreview = (
          <div>
            <DriveFile style={largeIconStyles} />
            <h5 className="text-center pt-3"><FormattedMessage id="PREVIEW_UNAVAILABLE" /></h5>
            <div className="text-center text-muted"><FormattedMessage id="PREVIEW_UNAVAILABLE_PROCESSING" /></div>
            <div className="text-center text-muted"><FormattedMessage id="PLEASE_TRY_AGAIN" /></div>
          </div>
        );
      } else if (!fileCleared) {
        documentPreview = (
          <Fragment>
            <Button
              color="white"
              size="sm"
              className={`p-0 ${buttonClasses.join(' ')}`}
              disabled={isLoading || isFormLocked || selectedDocument.isLegacyDocument}
              onClick={this.onClear}
            >
              <OutlineClear style={iconStyles} />
            </Button>
            <FileViewer
              fileType={(mimeType && mimeType.replace('.', '')) || ''}
              filePath={fileUrl || ''}
              unsupportedComponent={
                () => (
                  <UnsupportedError
                    messageId="FILE_COULD_NOT_BE_PREVIEWED"
                    ctaMessageId="DOWNLOAD"
                    onClick={() => downloadFromUrl(fileUrl, selectedDocument.fileName)}
                    classes="p-3"
                  />
                )
              }
              loadingComponent={() => (
                <div className="file-viewer-loading d-flex h-100 w-100" />
              )}
            />
          </Fragment>
        );
      } else {
        previewRowClasses.delete('h-75');
        previewColClasses.delete('border-dashed');
        documentPreview = (
          <FormGroup>
            <UploadDropzone
              onDrop={this.onDrop}
              onError={this.props.displayToaster}
              disabled={isSubmitting}
              fileSizeLimit={MAX_EMPLOYEE_DOC_UPLOAD_SIZE_IN_BYTES}
            />
          </FormGroup>
        );
      }
    }

    const formData = {
      initialValues: {
        title: selectedDocument.title || selectedDocument.filename,
        togglePrivate: selectedDocument.isPrivate,
      },
      validationSchema: Yup.object().shape({
        title: Yup.string().required('PLEASE_SPECIFY_A_DISPLAY_NAME'),
      }),
    };

    return (
      <Fragment>
        <Drawer
          modalClassName="drawer wide"
          toggle={this.handleDrawer}
          isOpen={drawerIsOpen}
          parentselector={APPLICATION_ROOT}
          backdrop={isLoading ? 'static' : true}
        >
          <DrawerHeader
            title="VIEW_DOCUMENT"
            toggle={this.handleDrawer.bind(this)}
            downloadAction={
              // Download button shouldn't be present for pending or processing documents
              selectedDocument.status &&
              selectedDocument.status.stepNumber !== PENDING_STEP_NUMBER &&
              selectedDocument.status.isProcessing !== true ?
                () => {
                  downloadFromUrl(fileUrl, selectedDocument.fileName);
                }
                :
                undefined
            }
          />
          <Formik
            initialValues={formData.initialValues}
            validateOnChange
            validateOnBlur
            validationSchema={formData.validationSchema}
            validate={values => !values.title || (fileCleared && !file)}
          >
            {({
                values,
                errors,
                touched,
              }) => (
                <Fragment>
                  <LockedModalBody className="p-4 d-flex flex-column">
                    <Form>
                      <FieldBuilder
                        formFieldData={[
                          {
                            key: 'document-information',
                            type: 'section',
                            data: {
                              title: 'DOCUMENT_INFORMATION',
                              renderSectionBreak: false,
                            },
                          },
                          {
                            key: 'company-employee-dropdowns',
                            type: 'component',
                            data: {
                              component: (
                                <Row key="company-employee-select">
                                  <Col xs={12} sm={6}>
                                    <FormGroup>
                                      <Label for="companyId"><FormattedMessage id="COMPANY" /></Label>
                                      <Field
                                        name="companyId"
                                        render={() => (
                                          <Select
                                            name="companyId"
                                            classNamePrefix="react-select-container"
                                            defaultValue={{
                                              value: selectedDocument.companyId,
                                              label: selectedDocument.companyName,
                                            }}
                                            className={
                                              errors.companyId && touched.companyId
                                                ? 'form-control is-invalid p-0'
                                                : 'react-select-container form-control p-0 mb-0 border-0'
                                            }
                                            isDisabled
                                            components={{ LoadingIndicator: Spinner }}
                                          />
                                        )}
                                      />
                                      <ErrorMessage errorText={errors.companyId} />
                                    </FormGroup>
                                  </Col>
                                  <Col xs={12} sm={6}>
                                    <FormGroup>
                                      <Label for="employeeId"><FormattedMessage id="EMPLOYEE_NAME" /></Label>
                                      <Field
                                        name="employeeId"
                                        render={() => (
                                          <Select
                                            name="employeeId"
                                            classNamePrefix="react-select-container"
                                            defaultValue={{
                                              value: selectedDocument.employeeId,
                                              label: selectedDocument.employeeName,
                                            }}
                                            className={
                                              errors.employeeId && touched.employeeId
                                                ? 'form-control is-invalid p-0'
                                                : 'react-select-container form-control p-0 mb-0 border-0'
                                            }
                                            isDisabled
                                            components={{ LoadingIndicator: Spinner }}
                                          />
                                        )}
                                      />
                                      <ErrorMessage errorText={errors.employeeId} />
                                    </FormGroup>
                                  </Col>
                                </Row>
                              ),
                            },
                          },
                          {
                            key: 'display-name',
                            type: 'input',
                            data: {
                              colClasses: ['col-6'],
                              name: 'title',
                              label: 'DISPLAY_NAME',
                              placeholder: 'Display name',
                              disabled: isFormLocked,
                            },
                          },
                          {
                            key: 'category',
                            type: 'creatable',
                            data: {
                              dataCy: 'category',
                              colClasses: ['col-6'],
                              name: 'category',
                              label: 'CATEGORY',
                              defaultOptionValue: selectedDocument.category || '',
                              defaultOptionLabel: selectedDocument.category || '',
                              options: this.groupCategories(docCategories),
                              placeholder: 'Select a category',
                              maxLength: MAX_DOC_CATEGORY_LENGTH,
                              customStyles: {
                                singleValue: provided => ({ ...provided, whiteSpace: 'pre !important' }),
                                menu: provided => ({
                                  ...provided,
                                  whiteSpace: 'pre !important',
                                  maxHeight: '20vh',
                                  overflow: 'scroll',
                                }),
                                menuList: provided => ({ ...provided, maxHeight: '20vh' }),
                              },
                              onScrollToBottom: this.loadMoreCategories.bind(this),
                              isComponentLoading: loading[GET_EMPLOYEE_DOCUMENT_CATEGORIES] === 'loading',
                              disabled: isFormLocked,
                            },
                          },
                          {
                            key: 'document-options',
                            type: 'section',
                            data: {
                              title: 'DOCUMENT_OPTIONS',
                              requiredRole: ROLE_BASE_MANAGER,
                            },
                          },
                          {
                            key: 'toggle-private',
                            type: 'switch',
                            data: {
                              name: 'togglePrivate',
                              id: 'toggle-private',
                              label: 'Private',
                              disabled: isFormLocked,
                              requiredRole: ROLE_BASE_MANAGER,
                            },
                          },
                          {
                            key: 'document-preview-section',
                            type: 'section',
                            data: {
                              title: 'DOCUMENT_PREVIEW',
                            },
                          },
                          {
                            key: 'document-preview',
                            type: 'component',
                            data: {
                              component: (
                                <Row key="file-viewer" className={[...previewRowClasses].join(' ')}>
                                  <Col className={[...previewColClasses].join(' ')}>
                                    <div className={containerClasses.join(' ')}>
                                      {documentPreview}
                                    </div>
                                  </Col>
                                </Row>
                              ),
                            },
                          },
                        ]}
                        userRoles={userRoles}
                        errors={errors}
                        touched={touched}
                        locked={isFormLocked}
                        isSubmitting={isSubmitting}
                      />
                    </Form>
                  </LockedModalBody>
                  <ModalFooter>
                    <SubmitButton
                      onSubmit={() => this.onSubmit(values)}
                      defaultLabel="UPDATE_DOCUMENT"
                      submittingLabel="UPDATING_DOCUMENT"
                      disabled={
                        submitDisabled ||
                        isFormLocked ||
                        isSubmitting
                      }
                      submitting={isSubmitting}
                    />
                  </ModalFooter>
                </Fragment>
              )
            }
          </Formik>
        </Drawer>
      </Fragment>
    );
  }
}

EditEmployeeDocument.defaultProps = {
  drawerIsOpen: false,
  selectedDocument: {},
  docCategories: undefined,
};

EditEmployeeDocument.propTypes = {
  displayToaster: PropTypes.func.isRequired,
  loading: LOADING_PROP_TYPE.isRequired,
  drawerIsOpen: PropTypes.bool,
  toggleDrawer: PropTypes.func.isRequired,
  hideToaster: PropTypes.func.isRequired,
  clearLoadingState: PropTypes.func.isRequired,
  updateEmployeeDocument: PropTypes.func.isRequired,
  getDocCategoryList: PropTypes.func.isRequired,
  clearCategories: PropTypes.func.isRequired,
  userRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
  filePreview: PropTypes.shape({
    url: PropTypes.string,
    filename: PropTypes.string,
  }).isRequired,
  selectedDocument: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    fileName: PropTypes.string,
    category: PropTypes.string,
    uploadDate: PropTypes.string,
    extension: PropTypes.string,
    isPublishedToEmployee: PropTypes.bool,
    companyId: PropTypes.number,
    companyName: PropTypes.string,
    employeeId: PropTypes.number,
    employeeName: PropTypes.string,
    isOnboarding: PropTypes.bool,
    uploadedBy: PropTypes.string,
    isLegacyDocument: PropTypes.bool,
    isEsignatureDocument: PropTypes.bool,
    isHelloSignDocument: PropTypes.bool,
    status: PropTypes.shape({
      name: PropTypes.string,
      priority: PropTypes.number,
      stepNumber: PropTypes.number,
      isProcessing: PropTypes.bool,
    }),
  }),
  docCategories: PropTypes.shape({
    categories: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    })),
    recent: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    })),
    next: PropTypes.string,
  }),
};

export default connect(
  (state) => {
    const {
      resources: {
        docs: {
          filePreview,
          selectedDocument,
          docCategories,
        },
      },
      appState,
      user: {
        roles,
      },
    } = state;
    return {
      loading: appState.loading,
      drawerIsOpen: appState.drawer.isOpen,
      userRoles: roles,
      filePreview,
      selectedDocument,
      docCategories,
    };
  },
  {
    displayToaster: args => displayToaster(...args),
    toggleDrawer: open => toggleDrawer(open),
    hideToaster: location => hideToaster(location),
    clearLoadingState: action => clearLoadingState(action),
    getDocCategoryList: (next, companyId) => getEmployeeDocCategories(next, companyId),
    updateEmployeeDocument,
    clearCategories,
  }
)(hot(module)(EditEmployeeDocument));



// WEBPACK FOOTER //
// ./src/containers/EditEmployeeDocument/EditEmployeeDocument.jsx